import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { initializeApp, getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import OpenAI from 'openai';
import { chunkText } from '../utils/textChunker';
import { getFirestore, doc, getDoc, deleteDoc, updateDoc } from 'firebase/firestore';

interface DocumentRow {
    id: number;
    content: string;
    metadata: any;
    embedding: number[];
}

interface EmbeddingResponse {
    embedding: number[];
    error?: string;
}

interface FileMetadata {
    documentIds: number[];
    filename: string;
    uploadedAt: string;
}

interface ChatFiles {
    [key: string]: FileMetadata | null;
}

interface ChatData {
    files?: ChatFiles;
    [key: string]: any;
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export class DocumentService {
    private supabase: SupabaseClient;
    private openai: OpenAI;
    private maxRetries = 3;
    private initialRetryDelay = 1000; // 1 second
    private static instance: DocumentService;

    private base64UrlEncode(str: string): string {
        return btoa(str)
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');
    }

    private base64UrlDecode(str: string): string {
        // Add removed padding
        str = str.replace(/-/g, '+').replace(/_/g, '/');
        switch (str.length % 4) {
            case 0:
                break;
            case 2:
                str += '==';
                break;
            case 3:
                str += '=';
                break;
            default:
                throw new Error('Invalid base64url string');
        }
        return atob(str);
    }

    private arrayBufferToBase64Url(buffer: ArrayBuffer): string {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return this.base64UrlEncode(binary);
    }

    private async generateSupabaseJWT(firebaseUser: { uid: string; email?: string | null }): Promise<string> {
        const now = Math.floor(Date.now() / 1000);
        const payload = {
            aud: 'authenticated',
            role: 'authenticated',
            user_id: firebaseUser.uid,
            email: firebaseUser.email || '',
            exp: now + (60 * 60), // 1 hour expiry
            iat: now,
            sub: firebaseUser.uid,
            session_id: crypto.randomUUID() // Unique session identifier
        };

        const encoder = new TextEncoder();
        const jwtSecret = import.meta.env.SUPABASE_JWT_SECRET;
        
        if (!jwtSecret) {
            throw new Error('Supabase JWT secret not configured');
        }

        try {
            // Create base64url-encoded header and payload
            const header = { alg: 'HS256', typ: 'JWT' };
            const encodedHeader = this.base64UrlEncode(JSON.stringify(header));
            const encodedPayload = this.base64UrlEncode(JSON.stringify(payload));
            const dataToSign = `${encodedHeader}.${encodedPayload}`;

            // Import key and sign
            const key = await crypto.subtle.importKey(
                'raw',
                encoder.encode(jwtSecret),
                { name: 'HMAC', hash: 'SHA-256' },
                false,
                ['sign']
            );

            const signature = await crypto.subtle.sign(
                'HMAC',
                key,
                encoder.encode(dataToSign)
            );

            // Convert signature to base64url using proper binary handling
            const encodedSignature = this.arrayBufferToBase64Url(signature);

            const token = `${dataToSign}.${encodedSignature}`;
            console.log('Generated JWT token with length:', token.length);
            return token;
        } catch (error) {
            console.error('Error generating JWT:', error);
            throw new Error('Failed to generate JWT token');
        }
    }

    private async refreshSupabaseAuth(): Promise<void> {
        console.log('Refreshing Supabase auth...');
        
        // Get Firebase user
        const auth = getAuth(getApp());
        if (!auth.currentUser) {
            throw new Error('No user is currently signed in');
        }

        try {
            // Log Firebase user info
            console.log('Firebase user:', {
                uid: auth.currentUser.uid,
                email: auth.currentUser.email,
                isAnonymous: auth.currentUser.isAnonymous
            });

            // Generate JWT token using Firebase user info
            const jwt = await this.generateSupabaseJWT({
                uid: auth.currentUser.uid,
                email: auth.currentUser.email
            });

            // Log and validate JWT structure
            const [header, payload, signature] = jwt.split('.');
            if (!header || !payload || !signature) {
                throw new Error('Invalid JWT structure');
            }

            try {
                const decodedHeader = JSON.parse(this.base64UrlDecode(header));
                const decodedPayload = JSON.parse(this.base64UrlDecode(payload));
                
                console.log('JWT validation:', {
                    header: decodedHeader,
                    payload: {
                        ...decodedPayload,
                        exp: new Date(decodedPayload.exp * 1000).toISOString()
                    },
                    tokenParts: {
                        headerLength: header.length,
                        payloadLength: payload.length,
                        signatureLength: signature.length
                    }
                });

                // Validate JWT claims
                const now = Math.floor(Date.now() / 1000);
                if (decodedPayload.exp <= now) {
                    throw new Error('JWT has expired');
                }
                if (decodedPayload.aud !== 'authenticated') {
                    throw new Error('Invalid audience in JWT');
                }
                if (decodedPayload.role !== 'authenticated') {
                    throw new Error('Invalid role in JWT');
                }
            } catch (error) {
                console.error('JWT validation error:', error);
                throw new Error(`JWT validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }

            // Create Supabase client with anon key and JWT
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.SUPABASE_KEY;
            
            console.log('Creating Supabase client with:', {
                url: supabaseUrl,
                keyLength: supabaseKey?.length || 0,
                hasJwt: !!jwt
            });

            // Update the client's global headers with the new JWT
            this.supabase = createClient(
                supabaseUrl,
                supabaseKey,
                {
                    auth: {
                        autoRefreshToken: false,
                        persistSession: false
                    },
                    global: {
                        headers: {
                            Authorization: `Bearer ${jwt}`
                        }
                    }
                }
            );

            // Verify connection
            const { error: testError } = await this.supabase
                .from('documents')
                .select('id')
                .limit(1);

            if (testError) {
                console.error('Supabase connection test failed:', testError);
                throw new Error(`Connection test failed: ${testError.message}`);
            }

            console.log('Successfully initialized and tested Supabase client with JWT auth');
        } catch (error) {
            console.error('Error in refreshSupabaseAuth:', error);
            throw new Error(`Failed to initialize Supabase client: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    private constructor(supabaseUrl: string, supabaseKey: string) {
        console.log('Initializing DocumentService with URL:', supabaseUrl);
        
        // Initialize Supabase client with global configuration
        this.supabase = createClient(supabaseUrl, supabaseKey, {
            auth: {
                autoRefreshToken: false,
                persistSession: false,
                detectSessionInUrl: false
            },
            global: {
                headers: {}
            }
        });
        
        const apiKey = import.meta.env.VITE_SYSTEM_OPENAI_API_KEY;
        if (!apiKey) {
            throw new Error('OpenAI API key not found. Please configure SYSTEM_OPENAI_API_KEY in your environment variables.');
        }
        this.openai = new OpenAI({
            apiKey,
            dangerouslyAllowBrowser: true
        });
    }

    public static getInstance(): DocumentService {
        if (!DocumentService.instance) {
            DocumentService.instance = new DocumentService(
                import.meta.env.VITE_SUPABASE_URL,
                import.meta.env.VITE_SUPABASE_KEY
            );
        }
        return DocumentService.instance;
    }

    private async getEmbeddingWithRetry(text: string, retryCount = 0): Promise<number[]> {
        try {
            console.log('Generating embedding for text length:', text.length);
            const response = await this.openai.embeddings.create({
                model: "text-embedding-ada-002",
                input: text,
            });
            console.log('Embedding generated successfully');
            return response.data[0].embedding;
        } catch (error: any) {
            if (error?.status === 429 && retryCount < this.maxRetries) {
                const delay = this.initialRetryDelay * Math.pow(2, retryCount);
                console.log(`Rate limited. Retrying in ${delay}ms...`);
                await sleep(delay);
                return this.getEmbeddingWithRetry(text, retryCount + 1);
            }
            console.error('Error generating embedding:', error);
            throw error;
        }
    }

    async storeDocument(content: string, metadata: { filename: string; chatId: string; userId: string; [key: string]: any }) {
        try {
            console.log('Processing document:', metadata.filename);
            const chunks = chunkText(content);
            console.log(`Split into ${chunks.length} chunks`);
            const results = [];

            for (let i = 0; i < chunks.length; i++) {
                try {
                    console.log(`Processing chunk ${i + 1}/${chunks.length}`);
                    const chunk = chunks[i];
                    const embedding = await this.getEmbeddingWithRetry(chunk);
                    
                    console.log('Storing chunk in Supabase...');
                    await this.refreshSupabaseAuth();
                    
                    const { data, error } = await this.supabase
                        .from('documents')
                        .insert({
                            content: chunk,
                            metadata: {
                                ...metadata,
                                isChunk: true,
                                chunkIndex: i,
                                totalChunks: chunks.length,
                                originalFile: metadata.filename,
                                chatId: metadata.chatId,
                                userId: metadata.userId
                            },
                            embedding
                        })
                        .select()
                        .single();

                    if (error) {
                        // Log detailed error information
                        console.error('Supabase error details:', {
                            error,
                            statusCode: error.code,
                            message: error.message,
                            details: error.details,
                            hint: error.hint
                        });

                        // Try to refresh auth if it's an auth error
                        if (error.code === '401' || error.message?.includes('JWSError')) {
                            console.log('Attempting to refresh authentication...');
                            await this.refreshSupabaseAuth();
                            
                            // Retry the operation
                            console.log('Retrying operation after auth refresh...');
                            const retryResult = await this.supabase
                                .from('documents')
                                .insert({
                                    content: chunk,
                                    metadata: {
                                        ...metadata,
                                        isChunk: true,
                                        chunkIndex: i,
                                        totalChunks: chunks.length,
                                        originalFile: metadata.filename,
                                        chatId: metadata.chatId,
                                        userId: metadata.userId
                                    },
                                    embedding
                                })
                                .select()
                                .single();

                            if (retryResult.error) {
                                console.error('Operation failed after auth refresh:', retryResult.error);
                                throw new Error('Operation failed even after authentication refresh. Please try again.');
                            }

                            return retryResult;
                        } else if (error.code === '42501') {
                            throw new Error('Permission denied. You do not have access to perform this operation.');
                        } else {
                            throw new Error(`Operation failed: ${error.message}${error.details ? ` (${error.details})` : ''}`);
                        }
                    }
                    
                    console.log('Chunk stored successfully:', data.id);
                    results.push(data);

                    await sleep(200);
                } catch (error) {
                    console.error('Error processing chunk:', error);
                    throw error;
                }
            }

            // Store document IDs in Firebase
            const documentIds = results.map(result => result.id);
            const db = getFirestore();
            const chatRef = doc(db, 'users', metadata.userId, 'chatHistory', metadata.chatId);

            // Get current chat data and initialize files object if needed
            const chatData = (await getDoc(chatRef)).data() || {};
            if (!chatData.files) {
                chatData.files = {};
            }

            // Prepare file metadata
            const fileMetadata: FileMetadata = {
                documentIds,
                filename: metadata.filename,
                uploadedAt: new Date().toISOString()
            };

            // Update files object
            chatData.files[metadata.filename] = fileMetadata;

            // Update the entire chat document
            await updateDoc(chatRef, chatData);

            // Verify the update
            const verifyDoc = await getDoc(chatRef);
            console.log('Final chat data after update:', verifyDoc.data());

            console.log(`Successfully stored ${results.length} chunks with IDs:`, documentIds);
            return results;
        } catch (error) {
            console.error('Error storing document:', error);
            throw error;
        }
    }

    async searchDocuments(query: string, chatId: string | null = null, matchThreshold = 0.6, matchCount = 10) {
        try {
            console.log('Searching documents for query:', query);
            const queryEmbedding = await this.getEmbeddingWithRetry(query);
            
            await this.refreshSupabaseAuth();
            
            console.log('Searching Supabase with embedding...');
            const { data, error } = await this.supabase.rpc('match_documents', {
                query_embedding: queryEmbedding,
                match_threshold: matchThreshold,
                match_count: matchCount,
                chat_id: chatId
            });

            if (error) {
                console.error('Supabase search error:', error);
                throw error;
            }

            console.log(`Found ${data?.length || 0} matching documents`);
            return { data };
        } catch (error) {
            console.error('Error searching documents:', error);
            throw error;
        }
    }

    async deleteDocumentsByFilename(filename: string, chatId: string, userId: string) {
        try {
            console.log(`Starting deletion for file: ${filename} in chat: ${chatId}`);
            
            // Get document IDs from Firebase chat document
            const db = getFirestore();
            const chatRef = doc(db, 'users', userId, 'chatHistory', chatId);
            const chatSnapshot = await getDoc(chatRef);
            
            if (!chatSnapshot.exists()) {
                console.log('Chat document not found in Firebase');
                return 0;
            }
            
            const chatData = chatSnapshot.data() as ChatData;
            console.log('Retrieved chat data:', chatData);

            if (!chatData.files) {
                console.log('No files object found in chat data');
                return 0;
            }

            let documentIds: number[] = [];

            if (filename === '*') {
                // For wildcard, collect all document IDs from all files
                Object.values(chatData.files).forEach(fileEntry => {
                    if (fileEntry && typeof fileEntry === 'object' && 'documentIds' in fileEntry) {
                        documentIds.push(...(fileEntry as FileMetadata).documentIds);
                    }
                });
                console.log('Found all document IDs for chat:', documentIds);
            } else {
                // Get file metadata for specific file
                const fileEntry = chatData.files[filename];
                console.log('File entry to delete:', fileEntry);

                if (!fileEntry || typeof fileEntry !== 'object' || !('documentIds' in fileEntry)) {
                    const error = new Error(`No valid file metadata found for "${filename}"`);
                    console.error(error);
                    throw error;
                }

                documentIds = (fileEntry as FileMetadata).documentIds;
                console.log('Found document IDs:', documentIds);
            }

            if (documentIds.length === 0) {
                console.log('No documents to delete');
                return 0;
            }

            await this.refreshSupabaseAuth();

            console.log(`Deleting documents with IDs:`, documentIds);
            const { data, error } = await this.supabase
                .from('documents')
                .delete()
                .in('id', documentIds)
                .select();

            if (error) throw error;

            // Update Firebase files object
            if (filename === '*') {
                // For wildcard, clear all files
                await updateDoc(chatRef, {
                    files: {}
                });
                console.log('Cleared all file entries from chat document');
            } else {
                // For specific file, remove just that entry
                const currentData = chatSnapshot.data() as ChatData;
                const updatedFiles = { ...currentData.files };
                delete updatedFiles[filename];
                await updateDoc(chatRef, {
                    files: updatedFiles
                });
                console.log('Removed file entry from chat document:', filename);
            }

            const deletedCount = data?.length || 0;
            console.log(`Successfully deleted ${deletedCount} documents`);
            return deletedCount;
        } catch (error) {
            console.error('Error in deleteDocumentsByFilename:', error);
            throw error;
        }
    }

    async getUploadedFiles(chatId: string, userId: string) {
        try {
            // Get file metadata from Firebase
            const db = getFirestore();
            const chatRef = doc(db, 'users', userId, 'chatHistory', chatId);
            const chatSnapshot = await getDoc(chatRef);
            
            if (!chatSnapshot.exists()) {
                console.log('Chat document not found in Firebase');
                return [];
            }
            
            const chatData = chatSnapshot.data() as ChatData;
            console.log('Chat data from Firebase:', chatData);
            
            if (!chatData.files) {
                console.log('No files object found');
                return [];
            }

            // Filter out null values and map to ActiveFile format
            const activeFiles = Object.entries(chatData.files)
                .filter(([_, data]) => data !== null && typeof data === 'object' && 'filename' in data)
                .map(([_, data]) => ({
                    filename: (data as FileMetadata).filename,
                    uploadedAt: (data as FileMetadata).uploadedAt
                }));
            
            console.log('Processed active files:', activeFiles);
            return activeFiles;
        } catch (error) {
            console.error('Error getting uploaded files:', error);
            throw error;
        }
    }
}